import clsx from "clsx";
import { useMemo } from "react";

interface AvatarProps {
  /** A username or display name used to derive initials and the gradient hue. */
  name: string;
  /** Optional explicit initials (max 2 chars). Falls back to the first 2 letters of `name`. */
  initials?: string;
  size?: "xs" | "sm" | "md" | "lg";
  className?: string;
  /** Variant `instagram` uses Instagram's classic gradient; `brand` uses the MusoBoost gradient. */
  variant?: "brand" | "instagram" | "deterministic";
}

const SIZE_CLASSES: Record<NonNullable<AvatarProps["size"]>, string> = {
  xs: "h-6 w-6 text-[10px]",
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-14 w-14 text-base",
};

/**
 * Pick a deterministic gradient from `name` so the same account always shows the
 * same colour. The palette is intentionally vibrant - it visually matches the
 * "warm music app" mood without ever needing a network round-trip.
 */
const DETERMINISTIC_GRADIENTS = [
  "from-fuchsia-500 to-pink-500",
  "from-rose-500 to-orange-500",
  "from-amber-500 to-yellow-400",
  "from-emerald-500 to-teal-500",
  "from-sky-500 to-cyan-500",
  "from-indigo-500 to-violet-500",
  "from-pink-500 to-purple-500",
  "from-lime-500 to-emerald-500",
];

function hashString(input: string): number {
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = (h << 5) - h + input.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

export default function Avatar({
  name,
  initials,
  size = "md",
  className,
  variant = "deterministic",
}: AvatarProps): JSX.Element {
  const computedInitials = useMemo(() => {
    if (initials) return initials.slice(0, 2).toUpperCase();
    const clean = name.replace(/^@/, "").trim();
    if (!clean) return "?";
    const parts = clean.split(/[\s._-]+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return clean.slice(0, 2).toUpperCase();
  }, [name, initials]);

  const gradient = useMemo(() => {
    if (variant === "brand") return "from-brand-500 to-fuchsia-600";
    if (variant === "instagram") return "from-amber-400 via-rose-500 to-fuchsia-500";
    const idx = hashString(name) % DETERMINISTIC_GRADIENTS.length;
    return DETERMINISTIC_GRADIENTS[idx];
  }, [variant, name]);

  return (
    <span
      className={clsx(
        "inline-flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br font-semibold uppercase text-white ring-2 ring-slate-900/60 shadow-md",
        gradient,
        SIZE_CLASSES[size],
        className
      )}
      aria-hidden="true"
    >
      {computedInitials}
    </span>
  );
}
